import { OraclePrompt } from '@modules/oracle/store/types';
import { CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useCallback } from 'react';
import { WisdomConversationStep } from './WisdomConversationStep';

interface WisdomConversationStepsProps {
  prompt: OraclePrompt;
  isReadOnly: boolean;
  hasFinalAnswer: boolean;
}

export function WisdomConversationSteps({
  prompt,
  isReadOnly,
  hasFinalAnswer,
}: WisdomConversationStepsProps) {
  const steps = prompt?.assistantResponse?.steps;
  const stepAnswerByID = prompt.assistantResponse?.stepAnswerByID;

  const renderSteps = useCallback(() => {
    if (!steps?.length) {
      return (
        <Stack direction="row" alignItems="center">
          <Stack
            alignItems="center"
            justifyContent="center"
            width="40px"
            height="30px"
          >
            <CircularProgress size={20} />
          </Stack>
          <Typography variant="subtitle2" className="step-title">
            Understanding the question...
          </Typography>
        </Stack>
      );
    }

    const firstPendingStepIndex = steps.findIndex(
      (step) => stepAnswerByID?.[step.id]?.end === undefined,
    );
    const stepsToRender =
      firstPendingStepIndex === -1
        ? steps
        : steps.slice(0, firstPendingStepIndex + 1);

    return (
      <Stack spacing={1}>
        {stepsToRender?.map((step, index) => {
          const stepAnswer = stepAnswerByID?.[step.id];
          const isAnswerAvailableInPrevStep =
            index === 0 ? true : !!stepAnswerByID?.[steps[index - 1].id];

          return (
            <WisdomConversationStep
              key={step.id}
              isReadOnly={isReadOnly}
              promptID={prompt.id}
              assistantResponseStep={step}
              hasFinalAnswer={hasFinalAnswer}
              stepAnswer={stepAnswer}
              isDisabled={stepAnswer ? false : !isAnswerAvailableInPrevStep}
              stepID={step.id}
            />
          );
        })}
      </Stack>
    );
  }, [hasFinalAnswer, isReadOnly, prompt.id, stepAnswerByID, steps]);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        px: 1.5,
        py: 2,
      }}
    >
      {renderSteps()}
    </Paper>
  );
}
